import { set, result } from 'lodash'
import { formatISO } from 'date-fns'

import COMMON from '@/common'
import { CHURCH_REGISTRY_USER_DEFAULTS } from '@/common/defaults'
import { parseDateString, t } from '@/utils/helpers'

const MAPPINGS = {
  id: { map: false }, // The new user ID will be stored on the old user
  title: {
    map: 'bio.title',
    values: {
      Ms: COMMON.TITLE_OPTIONS.MS,
      Mr: COMMON.TITLE_OPTIONS.MR,
      Prof: COMMON.TITLE_OPTIONS.PROF,
      Dr: COMMON.TITLE_OPTIONS.DR,
      // All others map to empty
    },
  },
  gender: { map: false }, // Is the same as `sex`
  sex: {
    map: 'bio.gender',
    values: {
      F: COMMON.GENDER_OPTIONS.FEMALE,
      M: COMMON.GENDER_OPTIONS.MALE,
      N: COMMON.GENDER_OPTIONS.OTHER,
      // All others map to empty
    },
  },
  firstname: { map: 'bio.name' },
  surname: { map: 'bio.surname' },
  middlename: { map: 'bio.middle_name' },
  maidenname: { map: 'bio.maiden_name' },
  familystatus: { map: false }, // Doesn't exist on any user
  maritalstatus: {
    map: 'bio.marital_status',
    values: {
      Widowed: COMMON.MARITAL_STATUS_OPTIONS.WIDOWED,
      Married: COMMON.MARITAL_STATUS_OPTIONS.MARRIED,
      Single: COMMON.MARITAL_STATUS_OPTIONS.SINGLE,
      single: COMMON.MARITAL_STATUS_OPTIONS.SINGLE,
      Divorced: COMMON.MARITAL_STATUS_OPTIONS.DIVORCED,
      Deceased: COMMON.MARITAL_STATUS_OPTIONS.DECEASED,
      'Not known': COMMON.MARITAL_STATUS_OPTIONS.UNKNOWN,
    },
  },
  birthday: {
    map: 'bio.birthday',
    mapFunction(val) {
      return formatISO(parseDateString(val))
    },
  },

  fullhomephone: {
    map: `contact[${COMMON.CONTACT_TYPE_INDEXES.TELEPHONE}].contact`,
    mapFunction(val) {
      return val.replace('/', ' ')
    },
  },
  fullworkohone: {
    map: `contact[${COMMON.CONTACT_TYPE_INDEXES.WORK_TELEPHONE}].contact`,
    mapFunction(val) {
      return val.replace('/', ' ')
    },
  },
  fullcellphone: {
    map: `contact[${COMMON.CONTACT_TYPE_INDEXES.MOBILE}].contact`,
    mapFunction(val) {
      return val.replace('/', ' ')
    },
  },
  fullfaxno: {
    map: '_delete.fullfaxno',
    mapFunction(val) {
      return val.replace('/', ' ')
    },
  },
  fullfaxhome: {
    map: '_delete.fullfaxhome',
    mapFunction(val) {
      return val.replace('/', ' ')
    },
  },
  email: { map: `contact[${COMMON.CONTACT_TYPE_INDEXES.EMAIL}].contact` },

  address1: { map: `address[${COMMON.ADDRESS_TYPE_INDEXES.PHYSICAL}].line1` },
  address2: { map: `address[${COMMON.ADDRESS_TYPE_INDEXES.PHYSICAL}].suburb` },
  city: { map: `address[${COMMON.ADDRESS_TYPE_INDEXES.PHYSICAL}].city` },
  code: { map: `address[${COMMON.ADDRESS_TYPE_INDEXES.PHYSICAL}].postal_code` },
  country: { map: `address[${COMMON.ADDRESS_TYPE_INDEXES.PHYSICAL}].country` },

  postaladdress1: { map: `address[${COMMON.ADDRESS_TYPE_INDEXES.POSTAL}].line1` },
  postaladdress2: { map: `address[${COMMON.ADDRESS_TYPE_INDEXES.POSTAL}].suburb` },
  postalcity: { map: `address[${COMMON.ADDRESS_TYPE_INDEXES.POSTAL}].city` },
  postalcode: { map: `address[${COMMON.ADDRESS_TYPE_INDEXES.POSTAL}].postal_code` },
  postalcountry: { map: `address[${COMMON.ADDRESS_TYPE_INDEXES.POSTAL}].country` },

  occupation: { map: 'occupations[0].occupation' },
  employer: { map: 'occupations[0].employer' },

  churchbackground: { map: `churches[${COMMON.CHURCHES_INDEXES.BACKGROUND}].church` },
  memberstatus: {
    map: `churches[${COMMON.CHURCHES_INDEXES.STAMI}].member`,
    values: {
      Member: COMMON.MEMBER_STATUS_OPTIONS.MEMBER,
      'Non-Member': COMMON.MEMBER_STATUS_OPTIONS.NON_MEMBER,
      'Not applicable': COMMON.MEMBER_STATUS_OPTIONS.UNKNOWN,
      'Not Applicable': COMMON.MEMBER_STATUS_OPTIONS.UNKNOWN,
      Friend: COMMON.MEMBER_STATUS_OPTIONS.FRIEND,
      Terminated: COMMON.MEMBER_STATUS_OPTIONS.TERMINATED,
      Contact: COMMON.MEMBER_STATUS_OPTIONS.CONTACT,
      Deceased: COMMON.MEMBER_STATUS_OPTIONS.DECEASED,
    },
  },
  datejoined: {
    map() {
      return this.oldUser.memberstatus === 'Member'
        ? `churches[${COMMON.CHURCHES_INDEXES.STAMI}].joined`
        : `churches[${COMMON.CHURCHES_INDEXES.BACKGROUND}].joined`
    },
    mapFunction(val) {
      return formatISO(parseDateString(val))
    },
  },
  memberbibleverse: { map: `churches[${COMMON.CHURCHES_INDEXES.STAMI}].member_bible_verse` },
  membermemo: { map: `churches[${COMMON.CHURCHES_INDEXES.STAMI}].member_notes` },

  baptised: { map: false }, // Doesn't exist on any user,
  baptiseddate: {
    map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.BAPTISM}].date`,
    mapFunction(val) {
      return formatISO(parseDateString(val))
    },
  },
  baptisedpastor: { map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.BAPTISM}].pastor` },
  baptisedgodparents: { map: false }, // Doesn't exist on any user
  baptisedbibleverse: { map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.BAPTISM}].verse` },
  baptisedplace: { map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.BAPTISM}].place` },

  confirmed: {
    map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.CONFIRMATION}].date`,
    mapFunction(val) {
      return formatISO(parseDateString(val))
    },
  },
  confirmeddate: { map: false }, // Doesn't exist on any user
  confirmationplace: { map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.CONFIRMATION}].place` },
  confirmationbibleverse: { map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.CONFIRMATION}].verse` },
  confirmationpastor: { map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.CONFIRMATION}].pastor` },

  weddingdatechurch: {
    map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.WEDDING}].date`,
    mapFunction(val) {
      return formatISO(parseDateString(val))
    },
  },
  weddingdatemagistrate: {
    map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.WEDDING}].magistrate`,
    mapFunction(val) {
      return formatISO(parseDateString(val))
    },
  },
  weddingplace: { map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.WEDDING}].place` },
  weddingpastor: { map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.WEDDING}].pastor` },
  weddingbibleverse: { map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.WEDDING}].verse` },
  weddingregister: {
    map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.WEDDING}].note`,
    mapFunction(val) {
      return `${t('WEDDING_REGISTER')}: ${val}`
    },
  },

  deathdate: {
    map: 'bio.death_date',
    mapFunction(val) {
      return formatISO(parseDateString(val))
    },
  },
  furneralplace: { map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.FUNERAL}].note` },
  furneralservicedate: {
    map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.FUNERAL}].date`,
    mapFunction(val) {
      return formatISO(parseDateString(val))
    },
  },
  furneralserviceplace: { map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.FUNERAL}].place` },
  furneralpastor: { map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.FUNERAL}].pastor` },
  furneralbibleverse: { map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.FUNERAL}].verse` },

  dedicationdate: {
    map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.DEDICATION}].date`,
    mapFunction(val) {
      return formatISO(parseDateString(val))
    },
  },
  dedicationplace: { map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.DEDICATION}].place` },
  dedicationpastor: { map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.DEDICATION}].pastor` },
  dedicationgodparents: {
    map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.DEDICATION}].note`,
    mapFunction(val) {
      return `${t('DEDICATION_GODPARENTS')}: ${val}`
    },
  },
  dedicationbibleverse: { map: `church_services[${COMMON.CHURCH_SERVICE_INDEXES.DEDICATION}].verse` },
}

export const mapArchiveUserToRegistryUser = function(oldUser) {
  const newUser = JSON.parse(JSON.stringify(CHURCH_REGISTRY_USER_DEFAULTS))
  const oldKeys = Object.entries(oldUser)
  Object.keys(MAPPINGS).forEach((key) => (MAPPINGS[key].oldUser = oldUser)) // Ensure old user is set so we can access it if we need to

  oldKeys.forEach(([key, value]) => {
    if (!value) {
      return
    }

    const newKey = MAPPINGS[key] && result(MAPPINGS, `[${key}].map`) ? result(MAPPINGS, `[${key}].map`) : ''
    if (!newKey) {
      return
    }

    if (MAPPINGS[key].values) {
      const newMappingExists = MAPPINGS[key].values[value]
      if (newMappingExists) {
        set(newUser, newKey, newMappingExists)
      }
      // TODO: Potentially add logic to map invalid value to a new value
      return
    }
    if (MAPPINGS[key].mapFunction) {
      set(newUser, newKey, MAPPINGS[key].mapFunction(value))
      return
    }
    set(newUser, newKey, value)
  })
  return newUser
}

export const mapTitle = function(title) {
  switch (title) {
    case 'Mr':
      return COMMON.TITLE_OPTIONS.MR
    case 'Ms':
      return COMMON.TITLE_OPTIONS.MS
    case 'Professor':
      return COMMON.TITLE_OPTIONS.PROF
    case 'Dr':
      return COMMON.TITLE_OPTIONS.DR
    default:
      return title
  }
}

export const reverseMapTitle = function(title) {
  switch (title) {
    case COMMON.TITLE_OPTIONS.MR:
      return 'Mr'
    case COMMON.TITLE_OPTIONS.MS:
      return 'Ms'
    case COMMON.TITLE_OPTIONS.PROF:
      return 'Professor'
    case COMMON.TITLE_OPTIONS.DR:
      return 'Dr'
    default:
      return title
  }
}

export const mapGender = function(gender) {
  switch (gender) {
    case 'M':
      return COMMON.GENDER_OPTIONS.MALE
    case 'F':
      return COMMON.GENDER_OPTIONS.FEMALE
    case 'O':
      return COMMON.GENDER_OPTIONS.OTHER
    default:
      return gender
  }
}

export const reverseMapGender = function(gender) {
  switch (gender) {
    case COMMON.GENDER_OPTIONS.MALE:
      return 'M'
    case COMMON.GENDER_OPTIONS.FEMALE:
      return 'F'
    case COMMON.GENDER_OPTIONS.OTHER:
      return 'O'
    default:
      return gender
  }
}

export const reverseMapMartialStatus = function(maritalStatus) {
  switch (maritalStatus) {
    case COMMON.MARITAL_STATUS_OPTIONS.SINGLE:
      return 'Single'
    case COMMON.MARITAL_STATUS_OPTIONS.MARRIED:
      return 'Married'
    case COMMON.MARITAL_STATUS_OPTIONS.WIDOWED:
      return 'Widowed'
    case COMMON.MARITAL_STATUS_OPTIONS.DIVORCED:
      return 'Divorced'
    case COMMON.MARITAL_STATUS_OPTIONS.UNKNOWN:
      return 'Unknown'
    default:
      return maritalStatus
  }
}

export const mapMartialStatus = function(maritalStatus) {
  switch (maritalStatus) {
    case 'Single':
      return COMMON.MARITAL_STATUS_OPTIONS.SINGLE
    case 'Married':
      return COMMON.MARITAL_STATUS_OPTIONS.MARRIED
    case 'Widowed':
      return COMMON.MARITAL_STATUS_OPTIONS.WIDOWED
    case 'Divorced':
      return COMMON.MARITAL_STATUS_OPTIONS.DIVORCED
    case 'Unknown':
      return COMMON.MARITAL_STATUS_OPTIONS.UNKNOWN
    default:
      return maritalStatus
  }
}
